import { FC, useMemo } from 'react'
import { match } from 'ts-pattern'
import {
  Text,
  IconClock,
  Badge,
  Banner,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary14SemiBold,
  Spinner,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { AnimatePresence, motion } from 'framer-motion'
import { InboxListItemContent } from 'Automations/components'
import { InboxPreview } from '../InboxPreview/InboxPreview'
import { NodeTimeline } from 'Automations/components'
import {
  resolvedNodeDefinitionsAtom,
  useResolvedNodeDefinitions
} from 'Automations/nodeDefinitions'
import { useAtomValue } from 'jotai'
import { omit } from 'lodash/fp'
import { useLegacyWorkflow } from 'Automations/automationExecutions/hooks/useLegacyWorkflow'
import {
  adaptLegacyWorkflowToExecution,
  mapLegacyStatusToExecutionStatus
} from 'Automations/automationExecutions/legacyWorkflows/legacyWorkflow.adapters'
import { getTasksGroupedByNode } from 'Automations/automationExecutions/overview/getAllTasksGroupedByNode'
import { useExecutionTimeline } from 'Automations/automationExecutions/overview/useExecutionTimeline'
import { CollapsibleToggleItem } from 'Automations/automationExecutions/overview/CollapsibleToggleItem'
import { getWorkflowDescription } from 'Automations/automationExecutions/utils/getWorkflowDescription'
import { AutomationExecution } from 'Automations/automationExecutions/types'

export const LegacyExecutionPreview: FC<{
  workflowId: string | null
}> = ({ workflowId }) => {
  const { workflow, isPending } = useLegacyWorkflow({ workflowId })
  const resolvedNodeDefinitions = useAtomValue(resolvedNodeDefinitionsAtom)

  const adaptedExecution = useMemo(
    () => (workflow ? adaptLegacyWorkflowToExecution(workflow) : undefined),
    [workflow]
  )

  const nodes = useMemo(
    () => (adaptedExecution ? getTasksGroupedByNode(adaptedExecution) : []),
    [adaptedExecution]
  )

  const isRunning =
    workflow?.status === 'IN_PROGRESS' || workflow?.status === 'NEEDS_REVIEW'

  const {
    firstNode,
    collapsibleNodes,
    pinnedNodes,
    hasCollapsibleNodes,
    isExpanded,
    toggle,
    showWaitingItem
  } = useExecutionTimeline({ nodes, isRunning, executionId: workflowId })

  if (isPending || !resolvedNodeDefinitions) {
    return <InboxPreview.LoadingState />
  }

  if (!adaptedExecution || !workflow) {
    return <InboxPreview.EmptyState />
  }

  const { title, logo } = getWorkflowDescription(adaptedExecution)

  const statusBadge = match(mapLegacyStatusToExecutionStatus(workflow.status))
    .with('COMPLETED', () => <Badge sentiment="success">Completed</Badge>)
    .with('TERMINATED', 'FAILED', () => <Badge sentiment="error">Failed</Badge>)
    .with('RUNNING', () => null)
    .exhaustive()

  return (
    <InboxPreview
      header={
        <Grid
          alignItems="center"
          columnGap="8px"
          templateColumns="1fr auto"
          autoFlow="column"
        >
          <Flex alignItems="center" gap="8px">
            <InboxListItemContent.TaskIcon isSelected={false}>
              {logo}
            </InboxListItemContent.TaskIcon>
            <Text
              {...Primary14SemiBold}
              color={GreyGrey80}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {title}
            </Text>
            {statusBadge}
          </Flex>
        </Grid>
      }
    >
      <Box>
        {isRunning && (
          <Box
            width="calc(632px + 32px * 2)"
            marginX="auto"
            padding="32px 32px 0"
          >
            <Banner
              variant="warning"
              data-testid="automations.management.executionsHistory.legacyWorkflow.runningBanner"
            >
              This is a legacy workflow that cannot be completed. Please
              re-upload the document to start a new workflow.
            </Banner>
          </Box>
        )}
        <NodeTimeline>
          {firstNode && (
            <RenderNode
              node={firstNode}
              automationExecution={adaptedExecution}
              isLatestNodeExecution={
                !hasCollapsibleNodes &&
                pinnedNodes.length === 0 &&
                !showWaitingItem
              }
            />
          )}

          {hasCollapsibleNodes && (
            <>
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    {collapsibleNodes.map((node, idx) => (
                      <RenderNode
                        key={node.nodeId}
                        node={node}
                        automationExecution={adaptedExecution}
                        isLatestNodeExecution={
                          pinnedNodes.length === 0 &&
                          !showWaitingItem &&
                          idx === collapsibleNodes.length - 1
                        }
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <CollapsibleToggleItem
                isExpanded={isExpanded}
                onToggle={toggle}
                data-testid="automations.management.executionsHistory.executionSummary.collapsibleToggle"
              />
            </>
          )}

          {pinnedNodes.map((node, idx) => (
            <RenderNode
              key={node.nodeId}
              node={node}
              automationExecution={adaptedExecution}
              isLatestNodeExecution={
                !showWaitingItem && idx === pinnedNodes.length - 1
              }
            />
          ))}

          {showWaitingItem && (
            <NodeTimeline.Item icon={<IconClock />} showStem={false}>
              <NodeTimeline.Title>
                <Flex alignItems="center" gap="6px">
                  <Text {...Primary13Regular} color={GreyGrey60}>
                    Waiting for next step
                  </Text>
                  <Spinner size="xs" color={GreyGrey60} />
                </Flex>
              </NodeTimeline.Title>
            </NodeTimeline.Item>
          )}
        </NodeTimeline>
      </Box>
    </InboxPreview>
  )
}

const RenderNode = ({
  node,
  automationExecution,
  isLatestNodeExecution
}: {
  node: ReturnType<typeof getTasksGroupedByNode>[number]
  automationExecution: AutomationExecution
  isLatestNodeExecution: boolean
}) => {
  const ExecutionSummaryComponent =
    useResolvedNodeDefinitions()[node.nodeType]?.executionSummary
  if (!ExecutionSummaryComponent) {
    return null
  }

  return (
    <ExecutionSummaryComponent
      tasks={node.tasks}
      automationExecution={
        omit(['tasks'])(automationExecution) as Omit<
          AutomationExecution,
          'tasks'
        >
      }
      isLatestNodeExecution={isLatestNodeExecution}
      reviewableTasks={[]}
    />
  )
}
